/****************************************************
 * Module Name:  memoReturnManage.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useRef, useState } from "react";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import Constants from "../../utility/constants";
import MemoProxy from "../../utility/proxy/manager/memoProxy";
import QrScannerModal from "./qrScannerModal";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import MemoManageModal from "./memoManageModal";
import StoneDetailModal from "../common/stoneDetailModal";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import SellMemoModal from "./sellMemoModal";
import { LoadingButton } from "@mui/lab";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Select from "react-select";
import WatchlistProxy from "../../utility/proxy/manager/watchlistProxy";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import OfferModal from "./OfferModal";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import HoldMemoModal from "./holdStoneModal";
import { FaBoxOpen } from "react-icons/fa";
import { alpha, styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import colourchart from '../../images/ColorScheme.png'
import CommonStoneSelection from "../common/commonStoneSelection";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { IoCloudyNightOutline } from "react-icons/io5";
import { IoPartlySunnyOutline } from "react-icons/io5";
import PageTitle from "../common/pageTitle";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";
import WatchlistModal from "../watchlist/watchlistModal";
import Download from "@mui/icons-material/Download";
import { createExcelFile } from "../common/createExcelFile";

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const StyledConvertButton = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 100,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      fontSize: 15,
      padding: "4px 12px",
      '& .MuiSvgIcon-root': {
        fontSize: 16,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const innerTableStyleCSS = {
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    backgroundColor: 'grey',
  },
  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
    py: "4px",
  },
  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
    py: "12px",
  },
  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
    py: "18px",
  },

  fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
  "& .super-app-theme--AVAILABLE": {
    color: "black",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      color: "black",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--HOLD": {
    backgroundColor: "#B6B9D6",
    color: "black",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      backgroundColor: "#B6B9D6",
      color: "black",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--GREEN_HOLD": {
    color: "#080808",
    backgroundColor: "#B6D7A8",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      color: "#080808",
      backgroundColor: "#B6D7A8",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
  "& .super-app-theme--MEMO_OUT": {
    color: "#080808",
    backgroundColor: "#B6D7A8",
    fontSize: Constants.defaultTableFontSize,
    fontWeight: Constants.defaultFontWeight,
    "&:hover": {
      color: "#080808",
      backgroundColor: "#B6D7A8",
      fontSize: Constants.defaultTableFontSize,
      fontWeight: Constants.defaultFontWeight,
    },
  },
}

const MemoReturnManage = () => {

  const [memoId, setMemoId] = useState(null);
  const [status, setStatus] = useState("");

  const [listData, setListData] = useState([]);
  const [memoIds, setMemoIds] = useState([]);
  const [customerIds, setCustomerIds] = useState([]);

  const [selectionModel, setSelectionModel] = useState([]);

  const [showQr, setShowQr] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  const [extraPacket, setExtraPacket] = useState("");
  const [showselectedSnackbar, setShowselectedSnackbar] = useState(false);
  const [checkPackages, setCheckPackages] = useState(true);
  const [loader, setLoader] = useState(false);
  const [scanRfLoad, setScanRfLoad] = useState(false);
  const [stoneId, setStoneId] = useState("");
  const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  // eslint-disable-next-line
  const [tags, setTags] = useState([]);
  const [isPermitted, setIsPermitted] = useState(false);
  const [multipleCustomerSnackbar, setMultipleCustomerSnackbar] = useState(false);
  const [customerValue, setCustomerValue] = useState("");
  const [customerLabel, setCustomerLabel] = useState("");
  const [memoOutLoader, setMemoOutLoader] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [fetchData, setFetchData] = useState(false);
  const [lastCalledMethod, setLastCalledMethod] = useState("");

  const [snackbarVariant, setSnackbarVariant] = useState("");
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [customerId, setCustomerId] = useState(Constants.loggedInUser.usertype === 'C' ? { label: Constants.loggedInUser.companyname, value: Constants.loggedInUser.id } : "");
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdData, setHoldData] = useState({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [customerData, setCustomerData] = useState({
    customer: "",
    customerOptions: [],
  });
  const [columnNames, setColumnNames] = React.useState([]);

  // Stock Summary
  const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
  const [selectedTotalSize, setSelectedTotalSize] = useState("0");
  const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
  const [selectedavgPrice, setSelectedavgPrice] = useState("0");
  const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");

  const [loading, setLoading] = useState(false);
  const [watchlistLoader, setWatchlistLoader] = useState(false);
  const [disableAllButtons, setDisableAllButtons] = useState(false);
  const [loadingProformaButton, setLoadingProformaButton] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [watchlistData, setWatchlistData] = useState([]);

  const extraPacketRef = useRef(extraPacket);

  useEffect(() => {
    extraPacketRef.current = extraPacket;
  }, [extraPacket]);

  // Create a connection
  const ws = useRef(null);

  // eslint-disable-next-line
  const [local_Permission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.OPERATION_MEMO_RETURNMANAGE
    )
  );

  const [sellPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTSALE
    )
  );

  const [offerPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTOFFER
    )
  );

  const [holdPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTHOLD

    )
  );

  useEffect(() => {
    if (loader || memoOutLoader || watchlistLoader || convertLoading) {
      setDisableAllButtons(true);
    } else {
      setDisableAllButtons(false);
    }
  }, [loader, memoOutLoader, watchlistLoader, convertLoading]);

  useEffect(() => {
    PageTitle("OPERATION_MEMO_RETURNMANAGE");
    checkPermission();
    prepareColumns();
  }, []);

  useEffect(() => {
    if (customerData.customer !== "" || isPermitted) {
      viewMemoOutStones();
    }
  }, [fetchData]);

  const checkPermission = () => {
    if (local_Permission.length > 0) {
      setIsPermitted(true)
      setFetchData(true);
    } else {
      getAllCustomerData();
    }
  }

  const getAllCustomerData = () => {
    let status = ["INPROGRESS"];

    MemoProxy.GetCustomersCreatedMemo(
      local_Permission.length > 0 ? Constants.loggedInUser.locationid : -1, //if permission show memo of only that location else of all location
      local_Permission.length > 0 ? -1 : Constants.loggedInUser.id, //if permission show memo created for all the customers else for all such customers which are attached to the logged in employee/broker/customer(himself)
      Constants.loggedInUser.usertype, status, (response) => {
        prepareUserData(response);
      });
  };

  const prepareUserData = (response) => {

    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.companyname,
      };
      optionsArray.push(option);
      return true;
    });

    setCustomerData({
      customerOptions: optionsArray,
      customer: optionsArray.length > 0 ? [optionsArray[0]] : []
    });
    setFetchData(true);

    return true;
  };
  const fullOptionToValue = (selectedData, mainData) => {
    let newArr = [];
    selectedData.map((data) => {
      if (data.value === "*") {
        return (mainData.map((option) => {
          if (option.value !== "*") {
            newArr.push(option.value);
            return true;
          }
          return true;
        })).join(",");
      }
      return true;
    })
    return Utility.GetValueArrayWithStarReplace(selectedData, newArr).join(",");
  };


  const handleSearch
    = (memoid) => {
      let status = "MEMO_OUT";
      setLoader(true);
      setRefresh(false);
      MemoProxy.GetAllMemoAtALocationByMemoId(
        isPermitted ? Constants.loggedInUser.locationid : -1,
        memoid,
        status,
        (response) => {
          if (response) {
            setListData(() => {
              let arr = [];
              response.map((res) => {
                let option = { ...res, isChecked: "false" };
                arr.push(option);
                return true;
              });
              return arr;
            });
            setLoader(false);
            loadTotalData(response);
            setShowQr(false);
          } else {
            setLoader(false);
            setErrorSnackbar(true);
            setErrorMessage(Constants.APiFailErrorMessage);
            setShowQr(false);
          }
        }
      );
      setLastCalledMethod("handleSearch");
    };

  const handleValidation = (data) => {
    //TODO do validation if needed here
    return true;
  };

  const handleStoneIds = (data) => {
    let stoneDataArray = [];
    let customerIdsArr = [];
    listData.map((iData) => {
      data.map((da) => {
        if (iData.stoneid === da) {
          stoneDataArray.push(iData.memoid);
          customerIdsArr.push(iData.customerid);
        }
        return true;
      });
      return true;
    });
    setMemoIds(stoneDataArray);
    setCustomerIds(customerIdsArr);
    return true;
  };

  const setTagsSelection = (tags) => {
    //tags  is array or string which are tag sample ['t1','t2']
    let lStockData = listData;
    let iNewSelected = 0;
    let iUnSelectedTags = 0;
    let selectedCheckBox = [];
    let tagsFound = [];
    let tagsNotFound = [];

    // tags = tags.map(tag => tag.replace(/^\['(.+?)'\]$/, '$1'));

    for (let loopCnt = 0; loopCnt < lStockData.length; loopCnt++) {
      if (lStockData[loopCnt].tag && tags.has(lStockData[loopCnt].tag)) {
        selectedCheckBox.push(lStockData[loopCnt].stoneid);
        tagsFound.push(lStockData[loopCnt].tag);
        iNewSelected++;
      }
    }

    tagsNotFound = [...tags].filter(tag => !tagsFound.includes(tag));
    iUnSelectedTags = tags.size - iNewSelected;

    setListData(() => {
      let selectedStones = [];
      let unSelectedStones = [];
      lStockData.forEach((res) => {
        if (selectedCheckBox.includes(res.stoneid)) {
          selectedStones.push({
            ...res,
            isChecked: "true",
          });
        } else {
          unSelectedStones.push({
            ...res,
            isChecked: "false",
          });
        }
      });
      return [...selectedStones, ...unSelectedStones];
    });
    setSelectionModel(prev => Array.from(new Set([...prev, ...selectedCheckBox])));
    handleStoneIds(selectedCheckBox);
    if (tagsNotFound.length > 0) {
      setExtraPacket(prev => Array.from(new Set([...prev, ...tagsNotFound])));
    }

    return {
      NewSelectedStones: iNewSelected,
      UnusedTags: iUnSelectedTags,
    };
  };

  const viewMemoOutStones = () => {
    stopScan();
    setMemoOutLoader(true);
    let selectedCustomers = Constants.loggedInUser.usertype === "C" ? Constants.loggedInUser.id.toString() : isPermitted ? "" : fullOptionToValue(customerData.customer, customerData.customerOptions);
    let statuss = "MEMO_OUT";

    MemoProxy.GetMemoOutStones(
      isPermitted ? Constants.loggedInUser.locationid : -1,
      Constants.loggedInUser.usertype,
      isPermitted ? -1 : Constants.loggedInUser.id,
      statuss,
      selectedCustomers === "" ? " " : selectedCustomers,
      (response) => {
        if (response) {
          setListData(() => {
            let arr = [];
            response.map((res) => {
              let option = { ...res, isChecked: "false" };
              arr.push(option);
              return true;
            });
            return arr;
          });
          setMemoOutLoader(false);
          loadTotalData(response);
        }
        else {
          setMemoOutLoader(false);
          setErrorSnackbar(true);
          setErrorMessage(Constants.APiFailErrorMessage);
        }
      }
    );
    setLastCalledMethod("viewMemoOut");
  }

  const getSummaryData = () => {

    let data = [];
    if (selectionModel && selectionModel.length > 0) {
      data = listData.filter((data) => selectionModel.includes(data.stoneid));
    } else {
      data = listData;
    }

    let noOfStones = 0;
    let totalSize = 0;
    let totalAmount = 0;
    let total = 0;

    data.forEach(item => {
      noOfStones += 1;

      if (Utility.isValueValid(item.size)) {
        totalSize += Number(item.size);
      }

      if (Utility.isValueValid(item.total)) {
        totalAmount += Number(item.total);
      }

      if (
        Utility.isValueValid(item.price) &&
        Utility.isValueValid(item.discount) &&
        Utility.isValueValid(item.size)
      ) {
        const effectivePrice = (100 * Number(item.price)) / (100 + Number(item.discount));
        total += Number(item.size) * effectivePrice;
      }
    });

    return {
      ID: noOfStones,
      CTS: (Number(totalSize)).toFixed(2),
      DIS: (Number((100 - (totalAmount / total) * 100) * -1)).toFixed(2),
      PPC: (Number(totalAmount / totalSize)).toFixed(2),
      TOTAL: (Number(totalAmount)).toFixed(2),
    };

  }

  const getExcelData = () => {
    let data = [];

    if (selectionModel && selectionModel.length > 0) {
      data = listData.filter((data) => selectionModel.includes(data.stoneid));
    } else {
      data = listData;
    }

    const excelData = data.map(item => {

      return {
        REPORT: item.certificateid,
        SHAPE: item.shape,
        ID: item.stoneid,
        CTS: item.size,
        COL: item.colortype === "WHITE" ? item.color : item.colordescriptions,
        CLA: item.clarity,
        CUT: item.cut ? item.cut : " ",
        POL: item.pol,
        SYM: item.sym,
        FLR: item.flo,
        MEASUREMENTS: item.measurements,
        DEP: !item.depthpercent ? "" : (Number(item.depthpercent)).toFixed(2),
        "TABLE%": !item.tablepercent ? "" : (Number(item.tablepercent)).toFixed(2),
        "CR ANG": !item.crownangle ? "" : (Number(item.crownangle)).toFixed(2),
        LAB: item.lab,
        RAP: item.rapnetprice,
        DIS: parseFloat((Number(item.discount)).toFixed(2)),
        PPC: parseFloat((Number(item.price)).toFixed(2)),
        TOTAL: parseFloat((Number(item.total)).toFixed(2)),
        LOCATION: item.location,
        STATUS: item.status,
        KEYTOSYMBOLS: item.keytosymbols ? item.keytosymbols : " ",
        MEDIA: {
          t: "s",
          v: "Media URL",
          l: {
            Target: item.stonemediaurl,
            Tooltip: "Click to open images & videos",
          }, TOTAL: (Number(item.total)).toFixed(2),
        },
        status: item.status ? item.status : "",
        holdstonecustomerid: item.holdstonecustomerid,
        holdstoneuserid: item.holdstoneuserid,
        holdstonebrokerid: item.holdstonebrokerid,
      };
    });

    const calculatedRow = getSummaryData();

    //addding last row for summary data
    const lastRow = Object.fromEntries(
      Constants.STOCK_EXCEL_KEYS.map(key => [key, calculatedRow[key] || ""])
    );

    //addding last row for summary data
    excelData.push(lastRow);

    createExcelFile(excelData, customerId, Constants.STOCK_EXCEL_COLUMNS, "PI-Stock.xlsx");
  }

  const addToWatchlist = (data) => {
    setWatchlistLoader(true);
    WatchlistProxy.AddToWatchlist(data, (res) => {
      if (res && res.status === "Success") {
        //Success
        setShowWatchlistModal(false);
        setWatchlistLoader(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Watchlist Added successfully");
        setSnackbarVariant("success");
      } else {
        // Failure
        setShowWatchlistModal(false);
        setWatchlistLoader(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage(res ? res.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
      }
    })
  };

  const createOffer = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.CreateOffer(data, usertype, (responseData) => {
      if (responseData) {
        viewMemoOutStones();
        setShowOfferModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Offer created successfully");
        setSnackbarVariant("success");
        setLoadingButton(false);
      } else {
        setShowOfferModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage(Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const holdStones = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.HoldStones(data, usertype, (responseData) => {
      if (responseData && responseData.status === "Success") {
        viewMemoOutStones()
        setShowSuccessSnackbar(true);
        setSuccessMessage("Hold Successful");
        setSnackbarVariant("success");
        setShowHoldModal(false);
        setLoadingButton(false);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
        setShowHoldModal(false);
      }
    });
  };

  const handleSelection = (data) => {
    const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, listData, selectionModel);

    setListData(tempStockData);
    setSelectionModel(tempSelectionModel);
    handleStoneIds(tempSelectionModel);
  }

  const stopScan = () => {
    if (scanRfLoad) setScanRfLoad(false);
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.close();
      ws.current.onclose = () => {
        getStoneIdByTagId();
      }
    }
  }

  const performRFScan = () => {

    if (!scanRfLoad && !ws.current) {
      ws.current = new WebSocket(Constants.RFID_API);
    }

    // If true that means we need to run stop_scan
    // Open WebSocket connection and start scanning
    if (scanRfLoad) {
      setScanRfLoad(() => !scanRfLoad);
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.close();
        ws.current.onclose = () => {
          getStoneIdByTagId();
        }
      }

      return;
    } else {
      if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
        // WebSocket doesn't exist or was closed — create a new one
        ws.current = new WebSocket(Constants.RFID_API);
        ws.current.onopen = () => {
          ws.current.send("START_SCAN");
          setScanRfLoad(prev => !prev);
        };
      } else if (ws.current.readyState === WebSocket.CONNECTING) {
        // It's still connecting — safely assign onopen
        ws.current.onopen = () => {
          ws.current.send("START_SCAN");
          setScanRfLoad(prev => !prev);
        };
      } else if (ws.current.readyState === WebSocket.OPEN) {
        // Already open, just send
        ws.current.send("START_SCAN");
        setScanRfLoad(prev => !prev);
      }
    }

    // Listen for incoming messages from WebSocket
    let lstTags = new Set();
    ws.current.onmessage = (e) => {
      let message = typeof e.data === 'string' ? e.data : String(e.data);
      if (message.includes("TAG: ")) {
        const parts = message.split("TAG: ");
        if (parts.length === 2) {
          const remaining = parts[1].trim(); // remove extra whitespace
          remaining.split(',').forEach(str => lstTags.add(str.trim())); // split and trim each item
        }

        if (listData.length > 0) {
          setTagsSelection(lstTags);
        }
      } else if (message.includes("SCAN_COMPLETED")) {
        ws.current.close();
        ws.current.onclose = () => {
          getStoneIdByTagId();
        }
        setScanRfLoad(prev => !prev);
      }
    };
  };

  const getStoneIdByTagId = () => {
    if (extraPacketRef.current.length > 0) {
      StoneProxy.GetStoneIdByTagId(extraPacketRef.current, (response) => {
        if (response) {
          let resultString = Object.entries(response)
            .map(([stoneid, tag]) => `${stoneid}: ${tag}`)
            .join(', ');

          if (Object.entries(response).length < extraPacketRef.current.length) {
            const usedTags = new Set(Object.values(response));
            const extraTags = extraPacketRef.current.filter(tag => !usedTags.has(tag));

            const extraTagString = extraTags.map(tag => `STONE_ID: ${tag}`).join(', ');
            resultString = resultString + ', ' + extraTagString;
          }

          setShowSuccessSnackbar(true);
          setSuccessMessage(`You have ${resultString} extra package on your RF Machine`);
          setSnackbarVariant("error");
          setExtraPacket([]);
          ws.current = null;
        } else {
          setShowSuccessSnackbar(true);
          setSuccessMessage(Constants.APiFailErrorMessage);
          setSnackbarVariant("error");
          ws.current = null;
          setExtraPacket([]);
        }
      })
    }
  }

  const findMemoByTags = (tags) => {

    let data = {
      status: "MEMO_OUT",
      tags: tags
    };
    setScanRfLoad(true);
    MemoProxy.FindMemoByTags(Constants.loggedInUser.locationid, data, (response) => {
      if (response) {
        setListData(response);
        setRefresh(true);
        setScanRfLoad(false);
        if (checkPackages) {
          if (tags.length > response.length) {
            setExtraPacket(tags.length - response.length);
            setCheckPackages(false);
          }
        }
        loadTotalData(response);
      } else {
        setErrorSnackbar(true);
        setErrorMessage(Constants.APiFailErrorMessage);
        setScanRfLoad(false);
      }
    });
  };

  const loadTotalData = (data) => {
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size

    data.forEach((iData) => {
      let pricePerCarat = iData.price
      totalSize += iData.size;
      totalPrice += (iData.size * pricePerCarat);
      total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

    });
    setNoOfSelectedStones(data.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
  };

  useEffect(() => {
    if (selectionModel.length <= 0) {
      loadTotalData(listData);
      return;
    }
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size
    let reassignTag = [];
    listData.forEach((iData) => {
      if (selectionModel.includes(iData.stoneid)) {
        reassignTag.push({ stoneid: iData.stoneid, status: "TAGGING", tag: null });
        let pricePerCarat = iData.price
        totalSize += iData.size;
        totalPrice += (iData.size * pricePerCarat);
        total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
      }
    });
    setNoOfSelectedStones(selectionModel.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
  }, [selectionModel]);

  const manageMemo = (data) => {
    setLoading(true);
    MemoProxy.SendMultipleMemo(Constants.loggedInUser.locationid, data, false, (responseData) => {
      if (responseData && responseData.status === "Success") {
        refresh
          ? findMemoByTags(tags)
          : lastCalledMethod === "handleSearch" ? handleSearch(memoId) : viewMemoOutStones();
        setShowReturnModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stone Returned Successfully");
        setSnackbarVariant("success");
        setLoading(false);
      } else {
        setErrorSnackbar(true);
        setErrorMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setShowReturnModal(false);
        setLoading(false);
      }
    });
    setLastCalledMethod("memoManage");
  };

  const sellStones = (data) => {
    setInvoiceLoading(true);
    MemoProxy.SellStonesByMemo(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        refresh
          ? findMemoByTags(tags)
          : lastCalledMethod === "handleSearch" ? handleSearch(memoId) : viewMemoOutStones();
        setInvoiceLoading(false);
        setShowSellModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stones sold successfully");
        setSnackbarVariant("success");
      } else {
        setShowSellModal(false);
        setInvoiceLoading(false);
        setErrorSnackbar(true);
        setErrorMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
      }
    });
  };

  const createProforma = (data) => {
    setLoadingProformaButton(true);
    MemoProxy.CreateProformaByMemo(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        refresh
          ? findMemoByTags(tags)
          : lastCalledMethod === "handleSearch" ? handleSearch(memoId) : viewMemoOutStones();
        setLoadingProformaButton(false);
        setShowSellModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage("Proforma created successfully");
        setSnackbarVariant("success");
      } else {
        setLoadingProformaButton(false);
        setShowSellModal(false);
        setErrorSnackbar(true);
        setErrorMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
      }
    });
  };

  const handleStoneDetailModal = (params) => {
    setStoneId(params.value);
    setShowStoneDetailModal(true);
  };

  const handleSellMemoModal = () => {
    stopScan();
    let stonetype;
    let flag = true;
    listData.map(iStone => {
      if (selectionModel.includes(iStone.stoneid) && flag) {
        if (stonetype && stonetype !== iStone.stonetype) {
          if (stonetype === 'N' || iStone.stonetype === 'N') {
            flag = false;
            setShowSnackbar(true);
            setSnackBarMessage("Stonetype mismatch. Select stones with same stonetype");
          }
        } else if (!stonetype) {
          stonetype = iStone.stonetype;
        }
      }
      return true;
    });

    if (!flag)
      return;

    let checkMultipleCustomer = customerIds.filter(
      (value, index, array) => array.indexOf(value) === index
    );
    if (checkMultipleCustomer.length > 1) {
      setMultipleCustomerSnackbar(true);
      return;
    }
    setCustomerValue(parseInt(checkMultipleCustomer.join("")));
    setCustomerLabel(
      () =>
        listData.find(
          (list) =>
            parseInt(list.customerid) ===
            parseInt(checkMultipleCustomer.join(""))
        ).customercompanyname
    );
    setShowSellModal(true);
  };

  const checkStones = (dayMemo) => {
    let stoneDataArray = [];
    listData.map(iData => {
      if (selectionModel.includes(iData.stoneid)) {
        if (dayMemo && iData.daymemo) {
          stoneDataArray.push(iData.stoneid);
          setSnackBarMessage("Please select all daymemo or overnight memo.");
        } else if (!dayMemo && !iData.daymemo) {
          stoneDataArray.push(iData.stoneid);
          setSnackBarMessage("Please select all daymemo or overnight memo.");
        }
      }
      return true;
    })

    if (stoneDataArray.length > 0) {
      setShowSnackbar(true);
    } else {
      setConvertLoading(true);
      MemoProxy.convertMemoType(selectionModel, dayMemo, (responseData) => {
        if (responseData && responseData.status === "Success") {
          refresh
            ? findMemoByTags(tags)
            : lastCalledMethod === "handleSearch" ? handleSearch(memoId) : viewMemoOutStones();
          setConvertLoading(false);
          setShowSuccessSnackbar(true);
          setSuccessMessage("Memo Type changed successfully");
          setSnackbarVariant("success");
        } else {
          setConvertLoading(false);
          setErrorSnackbar(true);
          setErrorMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        }
      });
    }
  }

  const columnData = {
    "8eaa8a03-d163-436d-8912-51ea06306f44": {
      field: "certificateid",
      headerName: "CERT",
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: renderCellExpand,
    },
    "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
      field: "stonetype",
      headerName: "STONETYPE",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => Constants.STONE_TYPE[params.value],
      renderCell: (params) => Constants.STONE_TYPE[params.value],
    },
    "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
      field: "shape",
      headerName: "SHAPE",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      align: "center",
      width: 80,
      renderCell: renderCellExpand,
    },
    "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
      field: "stoneid",
      headerName: "STONEID",
      headerAlign: "center",
      align: "center",
      width: 80,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
        <Button
          sx={{ textDecorationLine: "underline", color: "black" }}
          onClick={() => handleStoneDetailModal(params)}
        >
          {params.value}
        </Button>
        :
        <div>{params.value}</div>,
    },
    "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
      field: "stonestatus",
      headerName: "STONE STATUS",
      headerAlign: "center",
      align: "center",
      width: 120,
      renderCell: (params) => params.value === "HOLD" ? (<span style={{ fontSize: "14px" }}><b>{params.value}</b></span>) : params.value,
      cellClassName: (params) => params.row.stonestatus === "HOLD" ? (params.row.holduserid === params.row.userid && params.row.holdcustomerid === params.row.customerid ? `super-app-theme--GREEN_HOLD` : `super-app-theme--HOLD`) : `super-app-theme--${params.row.stonestatus.toUpperCase()}`,
    },
    "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
      field: "size",
      headerName: "SIZE",
      headerAlign: "center",
      align: "right",
      width: 40,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size),
    },
    "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
      field: "color",
      headerName: "COLOR",
      headerAlign: "center",
      align: "center",
      width: 65,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "d4992de9-92ab-4f9e-ba12-6c746e935611": {
      field: "colordescriptions",
      headerName: "COLOR DESCRIPTIONS",
      headerAlign: "center",
      align: "center",
      width: 160,
      renderCell: renderCellExpand,
    },
    "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
      field: "clarity",
      headerName: "CLARITY",
      headerAlign: "center",
      align: "center",
      width: 75,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "3597803f-8932-40c6-8de6-81006f4d6d5b": {
      field: "cut",
      headerName: "CUT",
      headerAlign: "center",
      align: "center",
      width: 50,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
      field: "pol",
      headerName: "POL",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "5541c182-21b8-496e-b342-b0b89f3d302f": {
      field: "sym",
      headerName: "SYM",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "8296e627-0be5-4a66-830f-ea6867cea137": {
      field: "flo",
      headerName: "FLO",
      headerAlign: "center",
      align: "center",
      width: 60,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "798a8663-0f0c-41be-8512-16dd60d5f25e": {
      field: "discount",
      headerName: "DIS",
      headerAlign: "center",
      align: "center",
      width: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
    },
    "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
      field: "rapnetprice",
      headerName: "RAP",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
    },
    "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
      field: "lab",
      headerName: "LAB",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "ab3e506e-bddb-456d-b967-922dcee74d56": {
      field: "location",
      headerName: "LOCATION",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "39c634f1-2644-4307-acbb-983b4de4229e": {
      field: "price",
      headerName: "PPC",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
    },
    "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
      field: "total",
      headerName: "TOTAL",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
    },
    "54fbd914-6ce7-44a8-9354-db6125ba1992": {
      field: "tag",
      headerName: "TAG",
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: (params) => (!params.value) ? "" : params.value,
    },
  };

  const prepareColumns = () => {
    let columnNames = [];

    columnNames.push({
      field: "memoid",
      headerName: "MEMO ID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 80,
    });

    Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
      if (columnData.hasOwnProperty(iColPermission)) {
        const columnsData = columnData[iColPermission];
        columnNames.push(columnsData);
        if (columnsData.field === "stoneid") {
          columnNames.push({
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "dateTime",
            valueGetter: (params) => {
              if (params.value) {
                return new Date(params.value);
              }
              return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
          });
          columnNames.push({
            field: "username",
            headerName: "CREATED BY",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
          });
          columnNames.push({
            field: "customercompanyname",
            headerName: "CUSTOMER COMPANY",
            headerAlign: "center",
            align: "center",
            width: 170,
            renderCell: (params) =>
              params.row.customerid + " | " + params.row.customercompanyname,
          });
          columnNames.push({
            field: "daymemo",
            headerName: "TYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            valueGetter: (params) => params.row.daymemo ? "DAY" : "OVERNIGHT",
            renderCell: (params) => params.row.daymemo ? "DAY" : "OVERNIGHT",
            type: "singleSelect",
            valueOptions: ["DAY", "OVERNIGHT"],
            filterOperators: Utility.combinedFilters(),
          });
          columnNames.push({
            field: "status",
            headerName: "MEMO STATUS",
            headerAlign: "center",
            align: "center",
            width: 170,
            renderCell: renderCellExpand,
          });
          columnNames.push({
            field: "memodetailstatus",
            headerName: "MEMO DETAIL STATUS",
            headerAlign: "center",
            align: "center",
            width: 150,
            renderCell: renderCellExpand,
          });
        }
      }
    })

    columnNames.push({
      field: "memomasterremark",
      headerName: "MEMO REMARK",
      headerAlign: "center",
      align: "center",
      width: 130,
      renderCell: renderCellExpand,
    });
    columnNames.push({
      field: "isChecked",
      headerName: "CHECKED",
      headerAlign: "center",
      align: "center",
      width: 150,
      renderCell: renderCellExpand,
    });
    columnNames.push({
      field: "memodetailremark",
      headerName: "STONE REMARK",
      headerAlign: "center",
      align: "center",
      width: 130,
      renderCell: renderCellExpand,
    });
    columnNames.push({
      field: "lastupdatedat",
      headerName: "LAST UPDATED",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });
    columnNames.push({
      field: "expectedreceive",
      headerName: "EXPECTED RECEIVE",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });
    columnNames.push({
      field: "updatedat",
      headerName: "UPDATED AT",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });
    columnNames.push({
      field: "givenat",
      headerName: "GIVEN AT",
      headerAlign: "center",
      align: "center",
      width: 150,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    });

    setColumnNames(columnNames);
  }

  const rows = listData;
  const columns = columnNames;

  // Color Scheme Drop Down Button
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseButton = () => {
    setAnchorEl(null);
  };

  // Custom Top Menu Bar
  function CustomToolbar() {
    const [convertMenu, setConvertMenu] = useState(undefined);
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          alignItems: "center",
          marginHorizontal: "5px",
          "&.MuiDataGrid-toolbarContainer": {
            padding: "0px 4px !important",
          },
        }}
      >
        <CommonStoneSelection
          title={"Enter Multiple Stone Ids"}
          handleSelection={handleSelection}
          buttonDisabled={listData.length === 0}
        />

        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
        {/* Custom Buttons */}
        {listData.length > 0 && (
          <Box>
            {loader ? (
              <CircularProgress size="1rem" />
            ) : (
              <Tooltip title="Reload List">
                <RefreshOutlinedIcon
                  onClick={() =>
                    lastCalledMethod === "memoManage"
                      ? findMemoByTags(tags)
                      : lastCalledMethod === "handleSearch"
                        ? handleSearch(memoId)
                        : viewMemoOutStones()
                  }
                  color="primary"
                  fontSize="medium"
                  sx={{ cursor: "pointer" }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        <Button
          component="label"
          variant="outlined"
          size="small"
          color="secondary"
          // sx={{ ml: "5px", }}
          startIcon={<Download />}
          onClick={() => {
            getExcelData();
          }}
        >
          EXCEL
        </Button>

        {isPermitted ? (
          <Box
            sx={{
              "& .Mui-disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                color: "rgba(0, 0, 0, 0.26) !important",
              },
            }}
          >
            <Tooltip title="View Memo-Out Stones">
              <LoadingButton
                sx={{
                  backgroundColor: "#0288d1 !important",
                }}
                variant="contained"
                component="label"
                loading={memoOutLoader}
                loadingPosition="start"
                startIcon={<VisibilityIcon size="large" />}
                size="small"
                onClick={() => viewMemoOutStones()}
              >
                View Memo-Out Stones
              </LoadingButton>
            </Tooltip>
          </Box>
        ) : null}

        {isPermitted ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginLeft: "5px",
            }}
          >
            <Box
              sx={{
                ".MuiButton-startIcon": {
                  marginLeft: "-2px",
                  marginRight: "4px",
                },
              }}
            >
              <LoadingButton
                id="demo-customized-convert-button"
                aria-controls={
                  convertMenu ? "demo-customized-convert-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={convertMenu ? "true" : undefined}
                variant="outlined"
                disableElevation
                disabled={selectionModel.length === 0 || disableAllButtons}
                onClick={(e) => setConvertMenu(e.currentTarget)}
                loading={convertLoading}
                loadingPosition="start"
                startIcon={<AutorenewIcon fontSize="small" />}
                sx={{
                  color: "#2e7d32",
                  borderRadius: "4px",
                  border: "1px solid rgba(46, 125, 50, 0.5)",
                  padding: "2px 2px 2px 8px",
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: "14px" }}>Convert</span>
                <KeyboardArrowDownIcon />
              </LoadingButton>
              <StyledConvertButton
                id="demo-customized-convert-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-convert-button",
                  style: {
                    maxHeight: 250,
                  },
                }}
                anchorEl={convertMenu}
                open={convertMenu}
                onClose={() => setConvertMenu(null)}
              >
                <MenuItem
                  onClick={() => {
                    stopScan();
                    checkStones(true);
                    setConvertMenu(null);
                  }}
                  disableRipple
                >
                  <IoPartlySunnyOutline size={18} />
                  <span style={{ marginLeft: "5px" }}>Day Memo</span>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    stopScan();
                    checkStones(false);
                    setConvertMenu(null);
                  }}
                  disableRipple
                >
                  <IoCloudyNightOutline size={18} />
                  <span style={{ marginLeft: "5px" }}>Overnight Memo</span>
                </MenuItem>
              </StyledConvertButton>
            </Box>
          </Box>
        ) : null}

        <Box>
          <LoadingButton
            loading={loader}
            loadingPosition="start"
            variant="contained"
            component="label"
            size="small"
            startIcon={<QrCodeScannerIcon size="large" />}
            onClick={() => {
              setShowQr(true);
              stopScan();
            }}
          >
            Scan
          </LoadingButton>
        </Box>

        {isPermitted ? (
          <Box>
            <Tooltip title={scanRfLoad ? "Stop Scan" : "Scan RFids"}>
              <LoadingButton
                variant="contained"
                component="label"
                startIcon={<QrCodeScannerIcon size="large" />}
                color={scanRfLoad ? "error" : "success"}
                size="small"
                onClick={() => performRFScan()}
              >
                {scanRfLoad ? "Stop Scan" : "Scan RF"}
              </LoadingButton>
            </Tooltip>
          </Box>
        ) : null}

        {isPermitted ? (
          <Box>
            <Tooltip title="Return Stones">
              <Button
                variant="contained"
                component="label"
                size="small"
                color="success"
                startIcon={<KeyboardReturnIcon size="large" />}
                disabled={selectionModel.length === 0 || disableAllButtons}
                onClick={(e) => {
                  stopScan();
                  setStatus("MEMO_RETURN");
                  setShowReturnModal(true);
                  return true;
                }}
              >
                Return
              </Button>
            </Tooltip>
          </Box>
        ) : null}

        <Box>
          {Utility.isReadAllowed(sellPermission) ? (
            <Tooltip title="Sell Stones">
              <Button
                variant="contained"
                component="label"
                size="small"
                startIcon={<DiamondOutlinedIcon size="large" />}
                color="success"
                disabled={selectionModel.length === 0 || disableAllButtons}
                onClick={handleSellMemoModal}
              >
                {Utility.GetBuyOrSell()}
              </Button>
            </Tooltip>
          ) : null}
        </Box>

        {!isPermitted && Utility.isReadAllowed(offerPermission) ? (
          <Tooltip title="Offer">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<LocalOfferIcon size="large" />}
              color="success"
              disabled={selectionModel.length < 1 || disableAllButtons}
              onClick={() => {
                let stonetype;
                let flag = true;
                listData.map((iStone) => {
                  if (selectionModel.includes(iStone.stoneid) && flag) {
                    if (stonetype && stonetype !== iStone.stonetype) {
                      if (stonetype === "N" || iStone.stonetype === "N") {
                        flag = false;
                        setShowSnackbar(true);
                        setSnackBarMessage(
                          "Stonetype mismatch. Select stones with same stonetype"
                        );
                      }
                    } else if (!stonetype) {
                      stonetype = iStone.stonetype;
                    }
                  }
                  return true;
                });

                if (!flag) return;

                try {
                  let customerCompanyName = "";
                  let flag = true;
                  selectionModel.forEach((stoneid) => {
                    if (flag) {
                      customerCompanyName = listData.find(
                        (data) => data.stoneid === stoneid
                      ).customercompanyname;
                      flag = false;
                    } else if (
                      customerCompanyName !==
                      listData.find((data) => data.stoneid === stoneid)
                        .customercompanyname
                    ) {
                      setShowSnackbar(true);
                      throw new Error("Different customer company name found");
                    }
                  });
                  let customer =
                    Constants.loggedInUser.usertype === "C"
                      ? {
                        label: Constants.loggedInUser.companyname,
                        value: Constants.loggedInUser.id,
                      }
                      : customerData.customerOptions.filter(
                        (data) => data.label === customerCompanyName
                      )[0];

                  setCustomerId(customer);
                  setShowOfferModal(true);
                } catch (e) { }
              }}
            >
              OFFER
            </Button>
          </Tooltip>
        ) : null}

        {!isPermitted && Utility.isReadAllowed(holdPermission) ? (
          <Tooltip title="Hold Stones">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<FaBoxOpen />}
              color="secondary"
              disabled={selectionModel.length < 1 || disableAllButtons}
              onClick={() => {
                try {
                  let customerCompanyName = "";
                  let flag = true;
                  let data = [];
                  selectionModel.forEach((stoneid) => {
                    let iData = listData.find(
                      (data) => data.stoneid === stoneid
                    );
                    data.push({
                      id: iData.stoneid,
                      status: iData.stonestatus,
                    });
                    if (flag) {
                      customerCompanyName = iData.customercompanyname;
                      flag = false;
                    } else if (
                      customerCompanyName !== iData.customercompanyname
                    ) {
                      setSnackBarMessage(
                        "To perform this action, Please select stones belongs to same customer company"
                      );
                      setShowSnackbar(true);
                      throw new Error("Different customer company name found");
                    }

                    // to check if the stone is alread held by the same customer
                    if (customerCompanyName === iData.holdcompanyname) {
                      setSnackBarMessage(
                        "Stones are already held by the same customer company"
                      );
                      setShowSnackbar(true);
                      throw new Error(
                        "Stone is already held by the same customer company"
                      );
                    }
                  });
                  let customer =
                    Constants.loggedInUser.usertype === "C"
                      ? {
                        label: Constants.loggedInUser.companyname,
                        value: Constants.loggedInUser.id,
                      }
                      : customerData.customerOptions.filter(
                        (data) => data.label === customerCompanyName
                      )[0];

                  setCustomerId(customer);
                  setShowHoldModal(true);
                  setHoldData(data);
                } catch (e) { }
              }}
            >
              Hold
            </Button>
          </Tooltip>
        ) : null}

        {isPermitted ||
          Constants.loggedInUser.usertype.toUpperCase() === "B" ? null : (
          <Tooltip title="Add to Watchlist">
            <LoadingButton
              color="success"
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              disabled={selectionModel.length < 1 || disableAllButtons}
              onClick={() => {
                if (selectionModel.length > 0) {
                  let dataArray = rows.filter((iRow) =>
                    selectionModel.includes(iRow.stoneid)
                  );
                  setWatchlistData(dataArray);
                  setShowWatchlistModal(true);
                }
              }}
              loading={watchlistLoader}
              loadingPosition="start"
              startIcon={<VisibilityOutlined size="large" />}
            >
              Watchlist
            </LoadingButton>
          </Tooltip>
        )}

        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }

  return (
    <div>
      {/* Modals */}
      <div>
        {showQr ? (
          <QrScannerModal
            show={showQr}
            setShow={setShowQr}
            handleValidation={handleValidation}
            handleSave={handleSearch}
            stoneId={selectionModel}
            sourceData={listData}
            setMemoId={setMemoId}
            loader={loader}
          />
        ) : null}

        {showReturnModal ? (
          <MemoManageModal
            show={showReturnModal}
            setShow={setShowReturnModal}
            handleValidation={handleValidation}
            handleSave={manageMemo}
            memoIds={memoIds.filter(
              (value, index, array) => array.indexOf(value) === index
            )}
            stoneId={selectionModel}
            sourceData={listData}
            status={status}
            loading={loading}
          />
        ) : null}

        {showSellModal ? (
          <div>
            <SellMemoModal
              show={true}
              setShow={setShowSellModal}
              handleValidation={handleValidation}
              handleInvoice={sellStones}
              handleProforma={createProforma}
              memoId={memoIds}
              stoneId={selectionModel}
              disabled={selectionModel.length < 1}
              user={
                Constants.loggedInUser.id +
                " | " +
                Constants.loggedInUser.companyname
              }
              customer={{ value: customerValue, label: customerLabel }}
              proformaButton={true}
              loadingProformaButton={loadingProformaButton}
              loading={invoiceLoading}
            />
          </div>
        ) : null}

        {showOfferModal ? (
          <OfferModal
            show={true}
            setShow={setShowOfferModal}
            handleValidation={handleValidation}
            handleOffer={createOffer}
            stoneId={selectionModel}
            customer={customerId}
            user={
              Constants.loggedInUser.id +
              " | " +
              Constants.loggedInUser.companyname
            }
            loadingOfferButton={loadingButton}
            notPermitted={false}
          />
        ) : null}

        {showHoldModal ? (
          <HoldMemoModal
            show={true}
            setShow={setShowHoldModal}
            handleValidation={handleValidation}
            handleSave={holdStones}
            data={holdData}
            stoneId={selectionModel}
            customer={customerId}
            loading={loadingButton}
          />
        ) : null}

        {showStoneDetailModal ? (
          <StoneDetailModal
            show={showStoneDetailModal}
            stoneId={stoneId}
            handleClose={() => setShowStoneDetailModal(false)}
          />
        ) : null}

        {showWatchlistModal ? (
          <WatchlistModal
            stoneData={watchlistData}
            handleSave={addToWatchlist}
            watchlistLoader={watchlistLoader}
            handleClose={() => setShowWatchlistModal(false)}
          />
        ) : null}

        {showSuccessSnackbar ? (
          <TabMenuSnackbar
            show={true}
            message={successMessage}
            variant={snackbarVariant}
            autoHide={Constants.SnackBarCloseTimer}
            title={snackbarVariant === "success" ? "Success" : "Failed"}
            handleClose={() => setShowSuccessSnackbar(false)}
          />
        ) : null}

        {showSnackbar ? (
          <TabMenuSnackbar
            show={showSnackbar}
            message={snackBarMessage}
            variant={"warning"}
            autoHide={Constants.SnackBarCloseTimer}
            title={"Warning"}
            handleClose={() => setShowSnackbar(false)}
          />
        ) : null}

        {showUserDetailModal ? (
          <UserDetailModal
            show={true}
            handleClose={() => setShowUserDetailModal(false)}
            handleSelect={(selectedOption) => {
              const selectedCustomers = customerData.customerOptions.filter(
                (opt) => selectedOption.includes(opt.value)
              );
              setCustomerData({
                ...customerData,
                customer: selectedCustomers,
              });
              setShowUserDetailModal(false);
            }}
            userids={customerData.customerOptions
              .filter((option) => option.value !== "*")
              .map((cust) => cust.value)}
            selectedIds={
              customerData.customer.length !== 0
                ? customerData.customer.map((iCustomer) => iCustomer.value)
                : []
            }
            usertype={"C"}
            loading={showUserDetailModal}
            isMulti={true}
          />
        ) : null}

        <TabMenuSnackbar
          show={errorSnackbar}
          message={errorMessage}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          title={"Failed"}
          handleClose={() => setErrorSnackbar(false)}
        />

        <TabMenuSnackbar
          show={multipleCustomerSnackbar}
          message={
            <Box>
              You can sell stones from multiple memos but for same customer,
              please select stones from anyone customer's memo
            </Box>
          }
          variant={"warning"}
          autoHide={false}
          handleClose={() => setMultipleCustomerSnackbar(false)}
          title="Warning"
        />

        <TabMenuSnackbar
          show={showselectedSnackbar}
          message={<Box>Stones selected successfully</Box>}
          variant={"success"}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => setShowselectedSnackbar(false)}
          title="Success"
        />
      </div>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: '5px 0px '
        }}
      >
        {!isPermitted ? (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box mx={2}>
                <Typography>
                  Customer Company{" "}
                  {Constants.loggedInUser.usertype !== "C" ? "" : " :- "}
                </Typography>
              </Box>

              {Constants.loggedInUser.usertype !== "C" ? (
                <Box
                  sx={{
                    gap: "5px",
                    display: "flex",
                    alignItems: "center",
                    maxWidth: "20vw",
                  }}
                >
                  <Select
                    menuPlacement="auto"
                    blurInputOnSelect={false}
                    isMulti
                    className="basic-single"
                    classNamePrefix="select"
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    value={customerData.customer}
                    isSearchable={true}
                    name="Customer"
                    menuPortalTarget={document.body}
                    styles={{
                      ...Constants.Styles,
                      container: (base) => ({
                        ...base,
                        minWidth: "10vw",
                      }),
                    }}
                    options={customerData.customerOptions}
                    onChange={(selectedOption) => {
                      setCustomerData({
                        ...customerData,
                        customer: selectedOption,
                      });
                    }}
                  />
                  <Tooltip title="View More Data">
                    <OpenInNewIcon
                      fontSize="medium"
                      color="action"
                      onClick={() => setShowUserDetailModal(true)}
                    />
                  </Tooltip>
                </Box>
              ) : (
                <Box>
                  <Typography
                    fontWeight={Constants.defaultFontWeightBold}
                    variant="h6"
                  >
                    {Constants.loggedInUser.companyname}
                  </Typography>
                </Box>
              )}
            </Box>


            <LoadingButton
              loading={memoOutLoader}
              sx={{
                backgroundColor: "#0288d1 !important",
              }}
              loadingPosition="start"
              variant="contained"
              onClick={() => viewMemoOutStones()}
              startIcon={<SearchOutlinedIcon size="large" />}
            >
              Search Memo-Out Stones
            </LoadingButton>
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
            flexBasis: 0,
            overflow: "hidden",
            color: "black",
            fontWeight: "normal",
            px: 1,
            textAlign: "center",
            paddingTop: "5px",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              justifyContent: "center",
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              No of Stones: <b>{noOfSelectedStones}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Total Weight: <b>{selectedTotalSize}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Total Price: <b>{selectedtotalPrice}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Avg Price: <b>{selectedavgPrice}</b>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: Constants.defaultTableFontSize }}
            >
              Avg Discount: <b>{selectedAvgDiscount}</b>
            </Typography>
          </Stack>
        </Box>


        <Box
          id="demo-customized-button"
          aria-controls={open ? "demo-customized-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          variant="outlined"
          disableElevation
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            color: "#454545",
            borderColor: "#454545",
            borderRadius: "4px",
            border: "1px solid black",
            padding: "2px 0 2px 5px",
            cursor: "pointer",
          }}
        >
          <img
            src={colourchart}
            alt="Color Scheme"
            style={{
              height: "24px",
              width: "24px",
            }}
          />
          <KeyboardArrowDownIcon />
          <StyledMenu
            id="demo-customized-menu"
            MenuListProps={{
              "aria-labelledby": "demo-customized-button",
              style: {
                maxHeight: 250,
              },
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseButton}
          >
            <MenuItem onClick={handleCloseButton} disableRipple>
              <span
                style={{
                  height: "15px",
                  width: "15px",
                  backgroundColor: "#000",
                  borderRadius: 20,
                  margin: 10,
                }}
              ></span>
              Available
            </MenuItem>
            {/* <MenuItem onClick={handleCloseButton} disableRipple>
                <span style={{ height: "15px", width: "15px", backgroundColor: "#b6b9d6", borderRadius: 20, margin: 10 }}></span>Hold (Another Customer)
              </MenuItem> */}
            <MenuItem onClick={handleCloseButton} disableRipple>
              <span
                style={{
                  height: "15px",
                  width: "15px",
                  backgroundColor: "#B6D7A8",
                  borderRadius: 20,
                  margin: 10,
                }}
              ></span>
              <b>HOLD</b>&nbsp;/ MEMO_OUT (Same Customer)
            </MenuItem>
          </StyledMenu>
        </Box>

      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          width: "100%",
          position: "relative",
          "& .MuiTablePagination-selectLabel": {
            marginTop: "15px",
          },
          "& .MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .css-cc8tf1": {
            fontWeight: Constants.defaultFontWeight,
          },
          height: "72vh",
        }}
      >
        <DataGridPro
          disableRowSelectionOnClick={true}
          sx={innerTableStyleCSS}
          density="compact"
          slots={{ toolbar: CustomToolbar }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.stoneid}
          checkboxSelection={true}
          onRowSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
            handleStoneIds(newSelectionModel);
          }}
          loading={loader}
          rowSelectionModel={selectionModel}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns id, the other columns will remain visible
                isChecked: false,
                updatedat: false,
                lastupdatedat: false,
                memodetailstatus: false,
                status: false,
              },
            },
            sorting: {
              sortModel: [{ field: "isChecked", sort: "asc" }],
            },
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
        />
      </Box>
    </div>
  );
};

export default MemoReturnManage;
